import { Box, Button, Flex, Modal, MultiSelect, Text, rem, BoxProps } from '@mantine/core';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useReorderTransactionsController } from './useReorderTransactionsController';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { CurrencyType, GetTransactionRes, TransactionTitle } from '@dealstack/shared/lib/generated';
import styled from 'styled-components';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	opened: boolean;
	onClose: () => void;
	transactionDate: string;
	personId: string;
	onRefreshParent: () => void;
	listRefetch?: () => void;
	topPosition: string;
	entityCurrency?: CurrencyType | null;
	initialTitles?: TransactionTitle[] | null;
}

export const ReorderTransactionsModal = ({
	opened,
	onClose,
	transactionDate,
	personId,
	onRefreshParent,
	listRefetch,
	topPosition,
	entityCurrency,
	initialTitles,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const {
		transactions,
		columns,
		getTransactionsListRequest,
		handleReorder,
		handleAutoReorder,
		isAutoReordering,
		showInterestFilter,
		interestOptions,
		titles,
		onTitlesChange,
	} = useReorderTransactionsController({
		transactionDate,
		personId,
		entityCurrency,
		initialTitles,
	});

	const firstTransaction = transactions?.[0];
	const SummaryTile = ({ label, value }: { label?: string; value?: string }) => (
		<StyledBoxTile>
			<Text size="B6Bold" mb="0.25rem">
				{label}
			</Text>
			<Text size="B4Light">{value}</Text>
		</StyledBoxTile>
	);

	return (
		<Modal.Root
			fullScreen
			opened={opened}
			onClose={onClose}
			closeOnEscape={true}
			sx={() => ({
				'.mantine-Modal-inner': {
					top: topPosition,
					zIndex: Z_INDEX.MODAL,
				},
			})}
		>
			<Modal.Content>
				<Modal.Header>
					<Modal.Title>
						<Text size="B3Bold">{tCapManage('transactions.reorder.modal.title')}</Text>
					</Modal.Title>
					<Modal.CloseButton size="lg" />
				</Modal.Header>
				<Modal.Body>
					{showInterestFilter && (
						<MultiSelect
							data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_REORDER_INTEREST_FILTER}
							label={tCapManage('transactions.table.columns.interest')}
							placeholder={tCapManage('transactions.table.filter.interest')}
							data={interestOptions}
							value={titles}
							onChange={onTitlesChange}
							clearable
							maw={rem('320px')}
							mt="1rem"
						/>
					)}

					{getTransactionsListRequest.isLoading && <InlineLoader />}

					{!getTransactionsListRequest.isLoading &&
						transactions &&
						transactions.length === 0 && (
							<p>{tCapManage('transactions.reorder.modal.no_transactions_error')}</p>
						)}

					{!getTransactionsListRequest.isLoading &&
						transactions &&
						transactions.length > 0 && (
							<Flex direction="column" gap="2rem" mt="1rem">
								<Flex direction="column" gap="1rem">
									{firstTransaction && (
										<Flex mb="md" gap="1rem">
											<SummaryTile
												label={tCapManage(
													'transactions.reorder.modal.date'
												)}
												value={formatBEDateToUiWithFallback(
													firstTransaction.date,
													DATE_FORMATS.CALENDAR_DATE,
													LONG_DASH
												)}
											/>
											<Button
												onClick={handleAutoReorder}
												loading={isAutoReordering}
												size="sm"
											>
												Force Reorder
											</Button>
										</Flex>
									)}
									{transactions.some((tx) => tx.transactionError) && (
										<InfoBox
											title={tCapManage(
												'transactions.reorder.modal.warning.title'
											)}
											description={tCapManage(
												'transactions.reorder.modal.warning.description'
											)}
											iconVariant="warning"
											rippleColor="red"
										/>
									)}
								</Flex>
								<MantineTable<GetTransactionRes>
									dataTestId={TEST_ELEMENT_IDS.CAP_TRANSACTION_REORDER_TABLE}
									getRowId={(row) => row.id}
									enableColumnResizing={true}
									onRowDropped={(onRowDroppedProps) => {
										const movedRowId = onRowDroppedProps.movedRowId;
										const destinationRowId =
											onRowDroppedProps.rowAboveId ??
											onRowDroppedProps.rowBelowId;

										if (!destinationRowId) {
											throw new Error(
												'Failed to reorder transaction: No destination row id'
											);
										}
										const isAbove = !onRowDroppedProps.rowAboveId;

										handleReorder(
											movedRowId,
											destinationRowId,
											isAbove,
											listRefetch
										);
									}}
									onIsFullScreenChange={() => {
										onRefreshParent();
									}}
									enableRowActions={false}
									enableTopToolbar={false}
									renderTopToolbar={null}
									manualPagination={false}
									manualFiltering={false}
									enablePagination={false}
									enableRowDragAndDrop={true}
									columns={columns}
									data={transactions || []}
									enableRowSelection={false}
									hideFooterRows={true}
									mantineTableBodyRowProps={({ row: _row, table: _table }) => {
										const hovered = false;
										const dragged = false;
										const isUp = false;

										const rowStyles: React.CSSProperties = {
											// A smoother and slightly slower transition
											transition: 'border-bottom-width 0.1s ease-in-out',
										};

										if (dragged) {
											rowStyles.borderBottom = `0.0625rem solid ${defaultTheme.v2.color.neutral[300]}`;
											rowStyles.borderLeft = 'none';
											rowStyles.borderRight = 'none';
											rowStyles.borderTop = 'none';
										}

										if (!dragged) {
											rowStyles.opacity = '1';
										}

										if (hovered) {
											if (isUp) {
												rowStyles.borderBottom = `2px solid ${defaultTheme.v3.colors.blue[600]}`;
												rowStyles.borderTop = `0.0625rem solid ${defaultTheme.v2.color.neutral[300]}`;
											} else {
												rowStyles.borderTop = `2px solid ${defaultTheme.v3.colors.blue[600]}`;
												rowStyles.borderBottom = `0.0625rem solid ${defaultTheme.v2.color.neutral[300]}`;
											}
											rowStyles.borderLeft = 'none';
											rowStyles.borderRight = 'none';
										}

										if (_row.original.transactionError) {
											rowStyles.backgroundColor =
												defaultTheme.v3.colors.red[0];
											rowStyles.borderBottom = `1px solid ${defaultTheme.v3.colors.red[2]}`;
										}

										return {
											style: rowStyles,
										};
									}}
								/>
							</Flex>
						)}
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};

const StyledBoxTile = styled(Box)<BoxProps>`
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 0.5rem;
	padding: 1rem;
	min-width: ${rem('256px')};
`;
